import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { onboardingRoute } from './onboarding-route';
import { OnboardingSessionStore } from './onboarding-session.store';
import { SessionEndReason } from './session-end-reason';

/**
 * La única salida del flujo: inactividad, token vencido o proceso completado (aunque lo haya completado otra persona).
 * Borra la sesión en memoria; el token sobrevive porque está en la URL.
 * - Completado es definitivo: va directo a `/onboarding/{token}/completed`, sin nada que revalidar.
 * - El resto vuelve a `/onboarding/{token}/rfc?reason=…`. Ahí el guard consulta el token y decide: en curso deja
 *   validar el RFC otra vez; vencido o completado, manda a su página.
 *
 * Solo actúa con una sesión activa (RFC validado y token en curso). Eso la hace idempotente, porque si dos detectores
 * disparan a la vez la segunda llamada encuentra la sesión ya borrada. Y deja los errores de antes de validar el RFC
 * en manos del guard y de la página del RFC.
 */
@Injectable({ providedIn: 'root' })
export class SessionExit {
  private readonly store = inject(OnboardingSessionStore);
  private readonly router = inject(Router);

  end(reason: SessionEndReason): void {
    if (!this.store.sessionActive()) {
      return;
    }
    // El token se lee antes de clear(): después ya no está para armar la URL.
    const token = this.store.token();
    this.store.clear();
    // replaceUrl: Atrás no vuelve al formulario.
    if (reason === 'completed') {
      void this.router.navigate(onboardingRoute(token, 'completed'), { replaceUrl: true });
      return;
    }
    // reload: si el cliente ya estaba en esa misma URL, la navegación corre igual.
    void this.router.navigate(onboardingRoute(token, 'rfc'), {
      queryParams: { reason },
      replaceUrl: true,
      onSameUrlNavigation: 'reload',
    });
  }
}
